import { useCallback, useState } from 'react';
import { testComposerApi } from '../services/api';
import type { Endpoint } from '@/types';
import type {
  ComposeTestsEndpointSpec,
  ComposeTestsRequest,
  SavedTestReference,
  TestScenario,
} from '@/types/endpointTests';
import { toComposerError } from '@/utils/composerErrors';

/** One generation turn kept for the in-session history. */
export interface TestComposerTurn {
  id: string;
  /** The guidance/instruction that produced this turn. */
  prompt: string;
}

/** Build the endpoint spec the generator needs — attached automatically,
 * never typed by the User. */
function buildEndpointSpec(endpoint: Endpoint): ComposeTestsEndpointSpec {
  return {
    path: endpoint.path,
    // The generated scenarios describe calls to /api/<accountSlug>/<path>, so
    // the owning slug travels with the spec (task 385).
    accountSlug: endpoint.accountSlug,
    method: endpoint.method,
    description: endpoint.description,
    input: endpoint.input,
    output: endpoint.output,
  };
}

/**
 * Drives test-scenario generation for an endpoint (task 365). The `compose-endpoint-tests` endpoint is synchronous
 * (responseQueued: false), so each call returns the scenarios in the same
 * response. Iteration continues the same collaboration by passing its
 * `collaborationId` — the Runner/Composer continuation contract.
 */
export function useTestComposer() {
  const [collaborationId, setCollaborationId] = useState<string | null>(null);
  const [scenarios, setScenarios] = useState<TestScenario[]>([]);
  /**
   * What the turn SAVED (task 795). The collaboration persists every scenario
   * itself, so these are references to rows that already exist — not a queue of
   * saves for this surface to perform.
   */
  const [savedTests, setSavedTests] = useState<SavedTestReference[]>([]);
  /** Set when the turn generated scenarios but could not save them. */
  const [saveError, setSaveError] = useState<string | null>(null);
  const [turns, setTurns] = useState<TestComposerTurn[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Raw backend error code behind `error` (e.g. 'entitlements_unresolved'),
  // kept for debugging while `error` shows friendly copy.
  const [errorCode, setErrorCode] = useState<string | null>(null);
  // The endpoint of the active generation chain, reused on each iterate.
  const [context, setContext] = useState<{ endpoint: Endpoint } | null>(null);

  const invoke = useCallback(
    async (body: ComposeTestsRequest, promptForHistory: string) => {
      setIsGenerating(true);
      setError(null);
      setErrorCode(null);
      try {
        const res = await testComposerApi.compose(body);
        const data = res.data.data;
        if (data.collaborationId) setCollaborationId(data.collaborationId);
        const out = data.output;
        if (out && Array.isArray(out.scenarios) && out.scenarios.length > 0) {
          setScenarios(out.scenarios);
          setSavedTests(Array.isArray(out.savedTests) ? out.savedTests : []);
          setSaveError(typeof out.saveError === 'string' ? out.saveError : null);
          setTurns((prev) => [
            ...prev,
            { id: crypto.randomUUID(), prompt: promptForHistory },
          ]);
          return out.scenarios;
        }
        setError("The generator didn't return any scenarios that time — try adjusting the guidance.");
        return null;
      } catch (err) {
        const composerError = toComposerError(err);
        setError(composerError.message);
        // Only surface a distinct code line when a friendly translation applied.
        setErrorCode(composerError.code !== composerError.message ? composerError.code : null);
        return null;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  /** Start a fresh generation for an endpoint (no collaborationId). */
  const generate = useCallback(
    (endpoint: Endpoint, guidance?: string, count?: number) => {
      // Reset any prior chain — this is a new generation.
      setCollaborationId(null);
      setScenarios([]);
      setSavedTests([]);
      setSaveError(null);
      setTurns([]);
      setContext({ endpoint });
      const trimmed = guidance?.trim();
      const body: ComposeTestsRequest = {
        endpointSpec: buildEndpointSpec(endpoint),
        // The target the generated tests are saved against (task 795). The spec
        // describes the service to the generator; this addresses the rows.
        endpointId: endpoint.id,
        ...(trimmed ? { guidance: trimmed } : {}),
        ...(count ? { count } : {}),
      };
      return invoke(body, trimmed || 'Generate test scenarios');
    },
    [invoke]
  );

  /**
   * Iterate with a follow-up instruction — re-sends the spec plus the
   * instruction and continues the same collaboration (passes collaborationId),
   * so the generator remembers what it already produced.
   */
  const iterate = useCallback(
    (instruction: string) => {
      if (!context || !collaborationId) return Promise.resolve(null);
      const body: ComposeTestsRequest = {
        endpointSpec: buildEndpointSpec(context.endpoint),
        endpointId: context.endpoint.id,
        guidance: instruction,
        collaborationId,
      };
      return invoke(body, instruction);
    },
    [context, collaborationId, invoke]
  );

  const reset = useCallback(() => {
    setCollaborationId(null);
    setScenarios([]);
    setSavedTests([]);
    setSaveError(null);
    setTurns([]);
    setContext(null);
    setError(null);
    setErrorCode(null);
  }, []);

  return {
    collaborationId,
    scenarios,
    savedTests,
    saveError,
    turns,
    isGenerating,
    error,
    errorCode,
    generate,
    iterate,
    reset,
  };
}
